import styled from 'styled-components';

import { colors } from 'constants/colors';

import {
    GetMicrostoreProductsQuery,
    GetSiteSetupQuery,
    MicrostoreColumnInventoryFragmentFragment,
    MicrostoreColumnSetupFragmentFragment,
    MicrostoreColumnWithShelvesFragmentFragment,
    SetupInstructionType,
} from 'data/__generated__';
import {
    isCheckBoxTemperatureInstruction,
    isCheckTemperatureInstruction,
    isHotDrinksInstruction,
    isManualReassortInstruction,
    isPhotosInstruction,
    isRemoveProductsInstruction,
    isStoreProductsInstruction,
} from 'pages/SiteSetups/utils/typePredicates';
import { ColumnInventorySection } from './inventory/ColumnInventorySection';
import { ColumnPhotosSection } from './photos/ColumnPhotosSection';
import { ColumnTemperatureSection } from './temperature/ColumnTemperatureSection';
import { StoreProductsSection } from './storeProducts/StoreProductsSection';
import { ManualReassortSection } from './manualReassort/ManualReassortSection';
import { SETUP_INSTRUCTION_TYPE_LABELS } from 'pages/SiteSetups/constants/types';

export const ColumnDetails = ({
    inventoryDate,
    products,
    selectedColumn,
    selectedColumnInventory,
    selectedColumnSetup,
    site,
    siteSetupDate,
}: {
    inventoryDate: string;
    products: GetMicrostoreProductsQuery['getMicrostoreProducts'];
    selectedColumn: MicrostoreColumnWithShelvesFragmentFragment;
    selectedColumnInventory: MicrostoreColumnInventoryFragmentFragment;
    selectedColumnSetup: MicrostoreColumnSetupFragmentFragment;
    site: GetSiteSetupQuery['getSiteSetup']['site'];
    siteSetupDate: string;
}) => {
    const temperatureInstruction = selectedColumnSetup.instructions.find(
        ({ type }) => type === SetupInstructionType.CheckTemperature,
    );
    const boxTemperatureInstruction = selectedColumnSetup.instructions.find(
        ({ type }) => type === SetupInstructionType.CheckBoxTemperature,
    );

    const removedProductsIntruction = selectedColumnSetup.instructions.find(
        ({ type }) => type === SetupInstructionType.RemoveProducts,
    );
    const photosInstruction = selectedColumnSetup.instructions.find(({ type }) => type === SetupInstructionType.Photos);
    const photosStartInstruction = selectedColumnSetup.instructions.find(
        ({ type }) => type === SetupInstructionType.PhotosStart,
    );
    const hotDrinksInstruction = selectedColumnSetup.instructions.find(
        ({ type }) => type === SetupInstructionType.HotDrinks,
    );
    const storeProductsInstruction = selectedColumnSetup.instructions.find(
        ({ type }) => type === SetupInstructionType.StoreProducts,
    );
    const manualReassortInstruction = selectedColumnSetup.instructions.find(
        ({ type }) => type === SetupInstructionType.ManualReassort,
    );

    return (
        <Container>
            {boxTemperatureInstruction && isCheckBoxTemperatureInstruction(boxTemperatureInstruction) ? (
                <ColumnTemperatureSection instruction={boxTemperatureInstruction} />
            ) : null}
            {temperatureInstruction && isCheckTemperatureInstruction(temperatureInstruction) ? (
                <ColumnTemperatureSection instruction={temperatureInstruction} />
            ) : null}

            <ColumnInventorySection
                column={selectedColumn}
                columnSetup={selectedColumnSetup}
                inventoryDate={inventoryDate}
                products={products}
                columnInventory={selectedColumnInventory}
                site={site}
                removeProductsInstruction={
                    removedProductsIntruction && isRemoveProductsInstruction(removedProductsIntruction)
                        ? removedProductsIntruction
                        : undefined
                }
                hotDrinksInstruction={
                    hotDrinksInstruction && isHotDrinksInstruction(hotDrinksInstruction)
                        ? hotDrinksInstruction
                        : undefined
                }
                siteSetupDate={siteSetupDate}
            />
            {storeProductsInstruction && isStoreProductsInstruction(storeProductsInstruction) ? (
                <StoreProductsSection column={selectedColumn} columnSetup={selectedColumnSetup} products={products} />
            ) : null}
            {manualReassortInstruction && isManualReassortInstruction(manualReassortInstruction) ? (
                <ManualReassortSection instruction={manualReassortInstruction} products={products} />
            ) : null}
            {photosStartInstruction && isPhotosInstruction(photosStartInstruction) ? (
                <ColumnPhotosSection
                    instruction={photosStartInstruction}
                    title={SETUP_INSTRUCTION_TYPE_LABELS[photosStartInstruction.type]}
                />
            ) : null}
            {photosInstruction && isPhotosInstruction(photosInstruction) ? (
                <ColumnPhotosSection
                    instruction={photosInstruction}
                    title={SETUP_INSTRUCTION_TYPE_LABELS[photosInstruction.type]}
                />
            ) : null}
        </Container>
    );
};

const Container = styled.div`
    border: 1px solid ${colors.pureBlack};
    border-top: 0px;
    border-radius: 0 0 10px 10px;
    padding: 10px;

    & > :not(:first-child) {
        margin-top: 20px;
        border-top: 1px solid ${colors.borderGrey};
    }
`;
